import {alpha, decomposeColor, getContrastRatio, hslToRgb, Theme} from "@mui/material/styles";

export interface SurveyControlColors {
    border: string
    hoverBorder: string
    accent: string
    focus: string
}

/** How the questions meet the page: the Design tab's Question style, and whether text reads on the page. */
export interface SurveyQuestionLayout {
    /** The Plain question style: no card around a question, a thin rule between questions. Cards when unset. */
    plain: boolean
    /** No background image, and the text colour reaches 4.5:1 against the page colour. Plain questions sit
     *  straight on the page only then; otherwise the frame draws one shared sheet in the surface colour. */
    textOnPage: boolean
}

export interface SurveyTextColors {
    primary: string
    secondary: string
    /** The accent for links, measured against this background alone. */
    link: string
}

/** The text colours twice: readable on the surface (cards, fields, the Plain sheet) and readable on the page. */
export interface SurveyTextSets {
    surface: SurveyTextColors
    page: SurveyTextColors
}

declare module '@mui/material/styles' {
    interface Theme {
        surveyControls?: SurveyControlColors
        surveyQuestionLayout?: SurveyQuestionLayout
        surveyText?: SurveyTextSets
    }
    interface ThemeOptions {
        surveyControls?: SurveyControlColors
        surveyQuestionLayout?: SurveyQuestionLayout
        surveyText?: SurveyTextSets
    }
}

const channels = (color: string) => decomposeColor(color.startsWith('hsl') ? hslToRgb(color) : color).values;

/** Resolve the color picker's alpha channel before calculating contrast. */
export const compositeSurveyColor = (color: string, background: string): string => {
    const foreground = channels(color);
    const behind = channels(background);
    const opacity = foreground[3] ?? 1;
    return `rgb(${foreground.slice(0, 3).map((value, index) => Math.round(value * opacity + behind[index] * (1 - opacity))).join(', ')})`;
};

const contrastOn = (color: string, backgrounds: string[]) => Math.min(...backgrounds.map(background => (
    getContrastRatio(compositeSurveyColor(color, background), background)
)));

export const surveyContrastColor = (color: string, backgrounds: string[], minimum: number): string => {
    if (contrastOn(color, backgrounds) >= minimum) return color;

    const base = compositeSurveyColor(color, backgrounds[0]);
    const failing = backgrounds.filter(background => getContrastRatio(base, background) < minimum);
    const candidates = ['#000000', '#FFFFFF'].flatMap(target => {
        if (contrastOn(target, failing) < minimum) return [];

        let low = 0;
        let high = 1;
        for (let iteration = 0; iteration < 16; iteration += 1) {
            const amount = (low + high) / 2;
            const candidate = compositeSurveyColor(alpha(target, amount), base);
            if (contrastOn(candidate, failing) >= minimum) high = amount;
            else low = amount;
        }

        const candidate = compositeSurveyColor(alpha(target, high), base);
        return contrastOn(candidate, backgrounds) >= minimum ? [{color: candidate, amount: high}] : [];
    });

    if (candidates.length) return candidates.sort((a, b) => a.amount - b.amount)[0].color;

    // Opposing page/card colors can make the requested ratio impossible for one shared color.
    return [base, '#000000', '#FFFFFF'].sort((a, b) => contrastOn(b, backgrounds) - contrastOn(a, backgrounds))[0];
};

export const surveyControls = (theme: Theme): SurveyControlColors => theme.surveyControls ?? {
    // The Build canvas also uses these controls, under the staff application's theme.
    border: theme.palette.divider,
    hoverBorder: theme.palette.primary.main,
    accent: theme.palette.primary.main,
    focus: theme.palette.primary.main,
};

export const surveyQuestionLayout = (theme: Theme): SurveyQuestionLayout => theme.surveyQuestionLayout ?? {
    // Outside the runner's theme, questions are the cards they have always been.
    plain: false,
    textOnPage: true,
};

/** The page and the card surface as they render, alpha resolved: for what must cover the content beneath it. */
export const surveySolidColors = (theme: Theme) => {
    const page = compositeSurveyColor(theme.palette.background.default, '#FFFFFF');
    return {page, surface: compositeSurveyColor(theme.palette.background.paper, page)};
};
